import JsSIP from "jssip";
import { toast } from "react-hot-toast";

// Configuration state
let sipUA = null;
let currentSession = null;
let peerConnectionConfig = null;
let remoteAudio = null;
let callListeners = [];
let connectionListeners = [];
let registrationListeners = [];
let fsmListeners = [];
const trackedCallIds = new WeakMap();
const sessionPeerConnectionConfigs = new WeakMap();

// Keep each call's ICE configuration private and independent from caller-owned
// objects and from options that JsSIP/the browser may mutate.
const clonePeerConnectionConfig = (config) => ({
  ...config,
  iceServers: config.iceServers.map((server) => ({
    ...server,
    ...(Array.isArray(server.urls) ? { urls: [...server.urls] } : {}),
  })),
});
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const parseNexusCallId = (value) => {
  if (typeof value !== "string") return null;
  const callId = value.trim().toLowerCase();
  return UUID_PATTERN.test(callId) ? callId : null;
};

export const nexusCallIdFromRequest = (request) => {
  if (!request || typeof request.getHeader !== "function") return null;
  return parseNexusCallId(request.getHeader("X-Nexus-Call-ID"));
};

const defaultConfig = {
  sip_display_name: "NexusDialer Agent",
  debug: import.meta.env.DEV,
};

/**
 * Initialize the SIP service with user credentials
 * @param {Object} config Configuration object
 * @param {string} config.username SIP username (extension)
 * @param {string} config.password SIP password
 * @param {string} config.domain SIP domain
 * @param {string} config.wsUri WebSocket URI for SIP server
 * @param {string} config.displayName Display name for SIP calls
 * @param {HTMLAudioElement} audioElement Remote audio element for call audio
 */
export const initializeSIP = (config, audioElement) => {
  // Clean up any existing connection
  disconnect();

  const requiredConfig = [
    config?.username,
    config?.password,
    config?.domain,
    config?.wsUri,
  ];
  if (
    !audioElement ||
    !requiredConfig.every(
      (value) => typeof value === "string" && value.trim().length > 0,
    )
  ) {
    return false;
  }

  let websocketUrl;
  try {
    websocketUrl = new URL(config.wsUri);
  } catch {
    return false;
  }
  if (!["ws:", "wss:"].includes(websocketUrl.protocol)) {
    return false;
  }

  remoteAudio = audioElement;

  // Configure audio element for optimal WebRTC performance
  remoteAudio.autoplay = true;
  remoteAudio.playsInline = true; // Important for iOS

  const sipConfig = {
    websocket_uri: websocketUrl.href,
    sip_uri: `sip:${config.username}@${config.domain}`,
    sip_password: config.password,
    sip_display_name: config.displayName || defaultConfig.sip_display_name,
    debug: config.debug ?? defaultConfig.debug,
  };

  // Set up JsSIP debug level
  if (sipConfig.debug) {
    JsSIP.debug.enable("JsSIP:*");
  } else {
    JsSIP.debug.disable();
  }

  // Log configuration (but mask the password)
  // Create socket with better error handling
  const socket = new JsSIP.WebSocketInterface(sipConfig.websocket_uri);

  // Add custom socket event listeners for better debugging
  socket.onconnect = () => {};
  socket.ondisconnect = () => {};
  socket.onerror = (error) => console.error("WebSocket error:", error);

  const iceServers = Array.isArray(config.iceServers)
    ? config.iceServers
    : [];
  peerConnectionConfig = clonePeerConnectionConfig({
    iceServers,
    iceTransportPolicy: config.forceTurn ? "relay" : "all",
    iceCandidatePoolSize: 1,
    bundlePolicy: "max-bundle",
    rtcpMuxPolicy: "require",
  });
  // Create UA configuration with enhanced options
  const uaConfig = {
    uri: sipConfig.sip_uri,
    password: sipConfig.sip_password,
    display_name: sipConfig.sip_display_name,
    sockets: [socket],
    register: true,
    register_expires: 300, // 5 minutes
    session_timers: true,
    user_agent: "NexusDialer WebClient",
    connection_recovery_min_interval: 2, // Minimum interval in seconds between reconnection attempts
    connection_recovery_max_interval: 30, // Maximum interval in seconds between reconnection attempts
  };

  try {
    // Create the SIP User Agent
    sipUA = new JsSIP.UA(uaConfig);

    // Set up event listeners
    setupUAListeners(sipUA);

    // Start the UA
    sipUA.start();

    return true;
  } catch (error) {
    disconnect();
    toast.error(
      "Failed to initialize SIP connection: " +
        (error.message || "Unknown error"),
    );
    return false;
  }
};

/**
 * Set up event listeners for the SIP UA
 */
const setupUAListeners = (ua) => {
  // Ignore events from a UA replaced by a reinitialization or logout.
  const on = (event, listener) => {
    ua.on(event, (...args) => {
      if (sipUA === ua) listener(...args);
    });
  };

  // Connection events
  on("connected", () => {
    notifyConnectionListeners("connected");
  });

  on("disconnected", () => {
    notifyConnectionListeners("disconnected");

    // Attempt to reconnect after a delay using an exponential backoff strategy
    // Start with 3 seconds, then increase delay up to 30 seconds
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 10;

    const attemptReconnect = () => {
      if (reconnectAttempts >= maxReconnectAttempts) {
        toast.error(
          "Could not reconnect to phone system after multiple attempts",
        );
        return;
      }

      reconnectAttempts++;
      const delay = Math.min(
        3000 * Math.pow(1.5, reconnectAttempts - 1),
        30000,
      );

      setTimeout(() => {
        if (sipUA === ua && !ua.isConnected()) {
          try {
            ua.start();
          } catch (err) {
            attemptReconnect(); // Try again
          }
        }
      }, delay);
    };

    attemptReconnect();
  });

  // Registration events
  on("registered", () => {
    // Registration state is shown by the call dock; no toast per re-register.
    notifyRegistrationListeners("registered");
  });

  on("unregistered", () => {
    notifyRegistrationListeners("unregistered");

    // If this was not due to an intentional stop/disconnect, try to re-register
    if (ua.isConnected()) {
      setTimeout(() => {
        if (sipUA !== ua || !ua.isConnected()) return;
        try {
          ua.register();
        } catch (err) {}
      }, 3000);
    }
  });

  on("registrationFailed", (data) => {
    notifyRegistrationListeners("failed", data.cause);

    // More user-friendly error message based on cause
    let errorMessage = "Phone system connection failed";

    if (data.cause === "Connection error") {
      errorMessage =
        "Unable to connect to the phone system server. Please check your network connection.";
    } else if (data.cause === "Authentication failed") {
      errorMessage =
        "Phone system authentication failed. Please check your credentials.";
    } else if (data.cause === "Request Timeout") {
      errorMessage =
        "Phone system connection timed out. The server may be unreachable.";
    } else if (data.cause === "Rejected") {
      errorMessage =
        "Phone system registration rejected. Your extension may already be in use elsewhere.";
    } else {
      errorMessage = `Phone system connection failed: ${data.cause}`;
    }

    // One deduplicated toast per failure cause; the dock keeps the state.
    toast.error(errorMessage, { id: "sip-registration" });

    // Log more detailed debugging information
    // Attempt to re-register after a delay for certain errors
    const retriableErrors = ["Request Timeout", "Connection error"];
    if (retriableErrors.includes(data.cause)) {
      setTimeout(() => {
        if (sipUA === ua && ua.isConnected()) {
          try {
            ua.register();
          } catch (err) {}
        }
      }, 5000);
    }
  });

  // Call events
  on("newRTCSession", (data) => {
    const session = data.session;
    const nexusCallId =
      session.direction === "incoming"
        ? nexusCallIdFromRequest(data.request)
        : null;

    if (nexusCallId) {
      trackedCallIds.set(session, nexusCallId);
    }
    // Handle incoming call
    if (session.direction === "incoming") {
      // If already in a call, reject
      if (currentSession) {
        session.terminate({
          status_code: 486, // Busy Here
          reason_phrase: "Busy Here",
        });
        return;
      }

      currentSession = session;
      sessionPeerConnectionConfigs.set(
        session,
        clonePeerConnectionConfig(peerConnectionConfig),
      );
      setupSessionListeners(session);

      // Notify listeners about incoming call
      notifyCallListeners("incoming", {
        ...(nexusCallId
          ? { id: nexusCallId, callId: nexusCallId }
          : {}),
        from: session.remote_identity.uri.user,
        displayName:
          session.remote_identity.display_name ||
          session.remote_identity.uri.user,
        cause: session.cause || "N/A",
        startTime: session.start_time || new Date().toISOString(),
        direction: "incoming",
      });
    } else if (session.direction === "outgoing") {
      currentSession = session;
      setupSessionListeners(session);
    }
  });
};

/**
 * Set up event listeners for a specific call session
 * @param {JsSIP.RTCSession} session The call session
 */
const setupSessionListeners = (session) => {
  const callId = trackedCallIds.get(session) || null;
  const on = (event, listener) => {
    session.on(event, (...args) => {
      if (currentSession === session) listener(...args);
    });
  };
  // Call progress events
  on("progress", (data) => {
    notifyCallListeners("progress", {
      ...(callId ? { id: callId, callId } : {}),
      to: session.remote_identity.uri.user,
      from: session.local_identity.uri.user,
      status: "progress",
    });
  });

  on("accepted", (data) => {
    notifyCallListeners("accepted", {
      ...(callId ? { id: callId, callId } : {}),
      to: session.remote_identity.uri.user,
      from: session.local_identity.uri.user,
      status: "accepted",
      startTime: session.start_time || new Date().toISOString(),
      duration: session.duration || 0,
    });
    toast.success("Registered extension connected");

    // Set up the media stream with multiple retry attempts
    const setupMediaStream = async () => {
      if (currentSession !== session) return;
      try {
        const remoteStream = new MediaStream();
        let trackAdded = false;

        // Try different methods to get the audio track
        // Method 1: JsSIP 3.x method
        if (data.stream && typeof data.stream.getReceiver === "function") {
          const receiver = data.stream.getReceiver("audio");
          if (receiver && receiver.track) {
            remoteStream.addTrack(receiver.track);
            trackAdded = true;
          }
        }

        // Method 2: Standard WebRTC method
        if (
          !trackAdded &&
          session.connection &&
          typeof session.connection.getReceivers === "function"
        ) {
          const receivers = session.connection.getReceivers();
          const audioReceiver = receivers.find(
            (r) => r.track && r.track.kind === "audio",
          );
          if (audioReceiver) {
            remoteStream.addTrack(audioReceiver.track);
            trackAdded = true;
          }
        }

        // Method 3: Legacy method
        if (
          !trackAdded &&
          session.getRemoteStreams &&
          session.getRemoteStreams().length > 0
        ) {
          const stream = session.getRemoteStreams()[0];
          const audioTracks = stream.getAudioTracks();
          if (audioTracks.length > 0) {
            remoteStream.addTrack(audioTracks[0]);
            trackAdded = true;
          }
        }

        // Method 4: Direct access to the peer connection (last resort)
        if (!trackAdded && session.connection) {
          try {
            // Try to get tracks directly from the peer connection
            const remoteTracks =
              session.connection.getRemoteStreams?.()?.[0]?.getTracks() || [];
            const audioTrack = remoteTracks.find(
              (track) => track.kind === "audio",
            );
            if (audioTrack) {
              remoteStream.addTrack(audioTrack);
              trackAdded = true;
            }
          } catch (err) {}
        }

        // Check if we got any tracks
        if (remoteStream.getTracks().length > 0) {
          if (remoteAudio) {
            // Set audio element properties for better compatibility
            remoteAudio.autoplay = true;
            remoteAudio.playsInline = true;
            remoteAudio.srcObject = remoteStream;

            // Try to play audio with retry logic
            try {
              // First attempt to play
              await remoteAudio.play();
            } catch (err) {
              if (currentSession !== session) return;
              // Show notification to user that interaction might be needed
              if (err.name === "NotAllowedError") {
                toast.error(
                  "Audio playback blocked. Please interact with the page to enable audio.",
                );

                // Add a listener for user interaction to retry playback
                const retryPlayback = async () => {
                  if (currentSession !== session) {
                    document.removeEventListener("click", retryPlayback);
                    document.removeEventListener("touchstart", retryPlayback);
                    return;
                  }
                  try {
                    await remoteAudio.play();
                    document.removeEventListener("click", retryPlayback);
                    document.removeEventListener("touchstart", retryPlayback);
                  } catch (playErr) {}
                };

                document.addEventListener("click", retryPlayback, {
                  once: true,
                });
                document.addEventListener("touchstart", retryPlayback, {
                  once: true,
                });
              } else {
                // For other errors, simply retry once
                setTimeout(async () => {
                  if (currentSession !== session) return;
                  try {
                    await remoteAudio.play();
                  } catch (retryErr) {
                    toast.error(
                      "Could not play audio. Check your device and browser permissions.",
                    );
                  }
                }, 1000);
              }
            }
          } else {
            toast.error("Audio playback error: No audio element");
          }
        } else {
          toast.warning("No audio received from call");

          // Last resort - try a delayed retry to get audio tracks
          setTimeout(() => {
            if (
              currentSession === session &&
              session.isEstablished() &&
              !remoteStream.getTracks().length
            ) {
              setupMediaStream();
            }
          }, 2000);
        }
      } catch (error) {
        toast.error("Failed to set up audio stream");
      }
    };

    // Start the media stream setup process
    setupMediaStream();
  });

  on("confirmed", () => {
    notifyCallListeners("confirmed", {
      ...(callId ? { id: callId, callId } : {}),
      to: session.remote_identity.uri.user,
      from: session.local_identity.uri.user,
      status: "confirmed",
    });
  });

  on("ended", () => {
    notifyCallListeners("ended", {
      ...(callId ? { id: callId, callId } : {}),
      to: session.remote_identity.uri.user,
      from: session.local_identity.uri.user,
      status: "ended",
      cause: session.cause || "Unknown",
      duration: session.duration || 0,
    });
    cleanupCurrentSession(session);
  });

  on("failed", (data) => {
    notifyCallListeners("failed", data.cause);
    cleanupCurrentSession(session);
  });

  // Handle DTMF
  on("newDTMF", (data) => {
    notifyCallListeners("dtmf", data.dtmf.tone);
  });

  // Handle mute state changes
  on("muted", () => {
    notifyCallListeners("muted", true);
  });

  on("unmuted", () => {
    notifyCallListeners("muted", false);
  });
};

/**
 * Clean up the current session
 */
const cleanupCurrentSession = (session = currentSession) => {
  if (session) sessionPeerConnectionConfigs.delete(session);
  if (currentSession !== session) return;
  if (remoteAudio) {
    remoteAudio.srcObject = null;
  }
  currentSession = null;
};


/**
 * Answer an incoming call
 * @returns {boolean} True if call was answered, false otherwise
 */
export const answerCall = () => {
  if (!currentSession || currentSession.direction !== "incoming") {
    return false;
  }

  try {
    // JsSIP 3.x consumes pcConfig on RTCSession.answer(), not on the UA.
    const pcConfig = sessionPeerConnectionConfigs.get(currentSession);
    if (!pcConfig) return false;
    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: clonePeerConnectionConfig(pcConfig),
    };

    currentSession.answer(options);
    return true;
  } catch (error) {
    toast.error("Failed to answer call");
    return false;
  }
};

/**
 * Hang up the current call
 * @returns {boolean} True if call was hung up, false otherwise
 */
export const hangupCall = () => {
  if (!currentSession) {
    return false;
  }

  try {
    currentSession.terminate();
    return true;
  } catch (error) {
    toast.error("Failed to hang up call");
    return false;
  }
};

/**
 * Toggle mute state of the current call
 * @returns {boolean} New mute state (true = muted, false = unmuted)
 */
export const toggleMute = () => {
  if (!currentSession) {
    return false;
  }

  try {
    if (currentSession.isMuted().audio) {
      currentSession.unmute();
      return false; // Not muted
    } else {
      // Ensure no changes to currentSession state
      currentSession.mute();
      return true; // Muted
    }
  } catch (error) {
    return currentSession.isMuted().audio;
  }
};

/**
 * Send DTMF tones
 * @param {string} tones DTMF tones to send (0-9, *, #)
 * @returns {boolean} True if tones were sent, false otherwise
 */
export const sendDTMF = (tones) => {
  if (!currentSession) {
    return false;
  }

  try {
    currentSession.sendDTMF(tones);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Check if there is an active call
 * @returns {boolean} True if there is an active call, false otherwise
 */
export const isInCall = () => {
  return !!currentSession;
};

/**
 * Check if SIP UA is registered
 * @returns {boolean} True if registered, false otherwise
 */
export const isRegistered = () => {
  return sipUA && sipUA.isRegistered();
};

/**
 * Check if SIP UA is connected
 * @returns {boolean} True if connected, false otherwise
 */
export const isConnected = () => {
  return sipUA && sipUA.isConnected();
};

/**
 * Get the current call status
 * @returns {string|null} Call status or null if no call
 */
export const getCallStatus = () => {
  if (!currentSession) return null;

  if (currentSession.isEstablished()) {
    return "established";
  } else if (currentSession.isInProgress()) {
    return "progress";
  } else if (currentSession.isEnded()) {
    return "ended";
  } else {
    return "unknown";
  }
};


/**
 * Add a call event listener
 * @param {Function} listener Callback function for call events
 * @returns {Function} Function to remove the listener
 */
export const addCallListener = (listener) => {
  callListeners.push(listener);
  return () => {
    callListeners = callListeners.filter((l) => l !== listener);
  };
};

/**
 * Add a connection event listener
 * @param {Function} listener Callback function for connection events
 * @returns {Function} Function to remove the listener
 */
export const addConnectionListener = (listener) => {
  connectionListeners.push(listener);
  return () => {
    connectionListeners = connectionListeners.filter((l) => l !== listener);
  };
};

/**
 * Add a registration event listener
 * @param {Function} listener Callback function for registration events
 * @returns {Function} Function to remove the listener
 */
export const addRegistrationListener = (listener) => {
  registrationListeners.push(listener);
  return () => {
    registrationListeners = registrationListeners.filter((l) => l !== listener);
  };
};

/**
 * Notify call listeners of an event
 * @param {string} event Event name
 * @param {any} data Event data
 */
const notifyCallListeners = (event, data) => {
  callListeners.forEach((listener) => {
    try {
      listener(event, data);
    } catch (error) {}
  });
};

/**
 * Notify connection listeners of an event
 * @param {string} status Connection status
 */
const notifyConnectionListeners = (status) => {
  connectionListeners.forEach((listener) => {
    try {
      listener(status);
    } catch (error) {}
  });
};

/**
 * Notify registration listeners of an event
 * @param {string} status Registration status
 * @param {string} cause Cause of registration failure (if applicable)
 */
const notifyRegistrationListeners = (status, cause) => {
  registrationListeners.forEach((listener) => {
    try {
      listener(status, cause);
    } catch (error) {}
  });
};

/**
 * Unregister and disconnect from SIP server
 */
export const disconnect = () => {
  const ua = sipUA;
  // Clear ownership before stop(), which can synchronously emit old events.
  sipUA = null;
  peerConnectionConfig = null;
  cleanupCurrentSession();
  remoteAudio = null;
  if (ua) ua.stop();
};

/**
 * Update the audio element used for calls
 * @param {HTMLAudioElement} audioElement New audio element
 * @returns {boolean} Success status
 */
export const updateAudioElement = (audioElement) => {
  if (!audioElement) {
    return false;
  }

  // Store current audio stream if any
  const currentStream = remoteAudio?.srcObject;

  // Update reference
  remoteAudio = audioElement;

  // Configure audio element properties
  remoteAudio.autoplay = true;
  remoteAudio.playsInline = true;

  // Restore stream if we had one
  if (currentStream) {
    remoteAudio.srcObject = currentStream;

    // Try to play
    remoteAudio.play().catch((error) => {});
  }

  return true;
};

export const notifyFsmListeners = (event, data) => {
  fsmListeners.forEach((listener) => {
    try {
      listener(event, data);
    } catch (_) {}
  });
};

export default {
  initializeSIP,
  answerCall,
  hangupCall,
  toggleMute,
  sendDTMF,
  isInCall,
  isRegistered,
  isConnected,
  getCallStatus,
  addCallListener,
  addConnectionListener,
  addRegistrationListener,
  notifyFsmListeners,
  disconnect,
  updateAudioElement,
};

